import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { createQueryKey } from "@/constants/query-keys";
import {
    getKpiByDate,
    getKpiByDateRange,
    getTeamMembersKpiByDate,
    getTeamMembersKpiByDateRange,
    getUserKpiByDate,
} from "@/hooks/queries/call.queries";

export const kpiKeys = {
    all: createQueryKey.kpis(),
    date: (date: string) => createQueryKey.kpiByDate(date),
    dateRange: (start: DateTime, end: DateTime) => createQueryKey.kpiByDateRange(start, end),
    teamMembers: createQueryKey.teamMembersKpis(),
    teamMembersDate: (date: string) => createQueryKey.teamMembersKpiByDate(date),
    teamMembersDateRange: (start: DateTime, end: DateTime) => createQueryKey.teamMembersKpiByDateRange(start, end),
};

/**
 * Hook to get KPI data for a specific date
 */
export const useKpiByDate = (date: string) => {
    return useQuery({
        queryKey: kpiKeys.date(date),
        queryFn: () => getKpiByDate(date),
        enabled: !!date,
    });
};

/**
 * Hook to get KPI data for multiple dates
 */
export const useKpiByDateRange = (start: DateTime, end: DateTime) => {
    return useQuery({
        queryKey: kpiKeys.dateRange(start, end),
        queryFn: () => getKpiByDateRange(start, end),
    });
};

/**
 * Hook to get team members KPI data for a specific date
 */
export const useTeamMembersKpiByDate = (date: string, teamId?: string) => {
    return useQuery({
        queryKey: kpiKeys.teamMembersDate(date),
        queryFn: () => getTeamMembersKpiByDate(date, teamId),
        enabled: !!date && !!teamId,
    });
};

/**
 * Hook to get team members KPI data for multiple dates.
 * Pass teamId for a single team, or "all" to aggregate across all teams.
 */
export const useTeamMembersKpiByDateRange = (
    start: DateTime,
    end: DateTime,
    teamId?: string,
    callPurpose?: string,
    options?: { enabled?: boolean }
) => {
    const resolvedTeamId = teamId === "all" ? undefined : teamId;
    return useQuery({
        queryKey: [...kpiKeys.teamMembersDateRange(start, end), teamId ?? "all", callPurpose ?? "all"],
        queryFn: () => getTeamMembersKpiByDateRange(start, end, resolvedTeamId, callPurpose),
        enabled: (options?.enabled ?? true) && !!teamId,
    });
};

/**
 * Hook to get KPI data for a specific user and date
 */
export const useUserKpiByDate = (date: string, userId: string) => {
    return useQuery({
        queryKey: ["userKpi", date, userId],
        queryFn: () => getUserKpiByDate(date, userId, DateTime.now().offset),
        enabled: !!date && !!userId,
    });
};

/**
 * Hook to get KPI data for a specific user across a date range in a single request.
 */
export const useUserKpiByDateRange = (
    start: DateTime,
    end: DateTime,
    userId?: string | null,
    callPurpose?: string,
    options?: { enabled?: boolean }
) => {
    return useQuery({
        queryKey: [...kpiKeys.dateRange(start, end), userId ?? "self", callPurpose ?? "all"],
        queryFn: () => getKpiByDateRange(start, end, userId ?? undefined, callPurpose),
        enabled: (options?.enabled ?? true) && !!userId,
    });
};
